import React, { useContext, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TimelineContent from "@mui/lab/TimelineContent";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import checkCircle from "assets/icons/check-circle-completed.svg";
import transparentCircle from "assets/icons/blank-circle.svg";
import useSortedCourseData from "hooks/useSortedCourseData";
import { Link, Navigate, useParams } from "react-router-dom";
import LoadingContext from "context/loadingContext/LoadingContext";
import { API_ENDPOINTS } from "config/ApiConfig";
import useGetRequest from "hooks/useGetRequest";
import LessonContext from "context/lessonContext/LessonContext";
import usePostRequest from "hooks/usePostRequest";
import forumIcon from "assets/forum/forum.svg"
import { MdForum } from "react-icons/md";
import { MdPushPin } from "react-icons/md";
import { MdChat } from "react-icons/md";
import { FaCertificate } from "react-icons/fa";
import CourseViewContext from "context/courseViewContext/CourseViewContext";

const CourseTimeline = () => {
  //state to store the slug
  const [storeSlug, setStoreSlug] = useState("");
  const [apiCalled, setApiCalled] = useState(false);
  const { chapter, lesson } = useParams();
  // Add a new state to track the selected lesson
  const [selectedLesson, setSelectedLesson] = useState(null);
  const { courseData } = useContext(CourseViewContext);
  
  const {
    setLessonInfo,
    lessonCompletionData,
    storeLessonIdentifiers,
    lessonIdentifiers,
    setSortedLessonData,
    sortedLessonData
  } = useContext(LessonContext);

  const {
    data: enrollmentData,
    loading: loadingEnrollmentData,
    error: enrollmentDataError,
    postData: enrollmentDataPayload,
  } = usePostRequest(API_ENDPOINTS.COURSE_ENROLLMENT_STATUS);

  //store the course chapters details
  const [courseChapter, setCourseChapter] = useState([]);
  //loading global context
  const { isAppLoading } = useContext(LoadingContext);

  // Update selected lesson when lesson changes in URL
  useEffect(() => {
    if (lesson) {
      // Find the lesson ID based on the lesson slug
      const foundLesson = sortedLessonData?.reduce((acc, chapter) => {
        const matchedLesson = chapter.lessons.find(l => l.lesson_slug === lesson);
        return matchedLesson ? matchedLesson : acc;
      }, null);

      if (foundLesson) {
        setSelectedLesson(foundLesson.lesson_id);
      }
    }
  }, [lesson, sortedLessonData]);

  useEffect(() => {
    if (chapter) {
      setStoreSlug(chapter);
    }
  }, [chapter]);

  //trigger the get req for the chapters timeline
  const {
    data: chapterData,
    error: chapterError,
    loading: loadingChapter,
    getData: chapterPayload,
  } = useGetRequest(API_ENDPOINTS.USER_COURSE_CHAPTER);

  useEffect(() => {
    const fetchChapter = async () => {
      if (!isAppLoading && storeSlug) {
        try {
          await chapterPayload(storeSlug);
        } catch (err) {
          // console.log("error with the get request api call");
        }
      }
    };
    fetchChapter();
  }, [isAppLoading, storeSlug]);

  useEffect(() => {
    if (!loadingChapter) {
      if (chapterError) {
        // console.log("error fetching the chapter error:", chapterError);
      } else if (chapterData) {
        //  Convert chapterData object to an array
        setCourseChapter(Object.values(chapterData));
        if (chapterData.courseStat && chapterData.courseStat.course_id) {
          storeLessonIdentifiers({
            course_id: chapterData.courseStat.course_id,
          });
        }
      }
    }
  }, [chapterData, loadingChapter, chapterError]);

  useEffect(() => {
    if (chapterData && chapterData.status === 'enrolled' && !apiCalled && lessonIdentifiers?.course_id) {
      enrollmentDataPayload({ course_id: lessonIdentifiers?.course_id, status: 'in-progress' });
      setApiCalled(true); // Mark the API as called
    }
  }, [chapterData, lessonIdentifiers, apiCalled]);

  // Always call the hook, but handle when data is not available
  const {
    sortedCourseData: sortedChapter,
    totalLessons,
    completedLessons,
    completionPercentage,
  } = useSortedCourseData(courseChapter);

  useEffect(() => {
    if (sortedChapter) {
      setSortedLessonData(sortedChapter);
    }
  }, [sortedChapter]);

  // Checks if the course is completed (all lessons completed)
  const isCourseCompleted = () => {
    if (!sortedLessonData || sortedLessonData.length === 0) {
      return false;
    }
    
    return sortedLessonData.every(chapter => 
      chapter.lessons && chapter.lessons.every(lesson => lesson.isCompleted)
    );
  };

  // Function to render the certificate accordion only when the course is completed
  const renderCertificateAccordion = () => {
    if (!isCourseCompleted()) {
      return null; // Don't show certificate if course is not completed
    }

    return (
      <Accordion className="course-timeline-wrapper__accordian">
        <AccordionSummary
          className="course-timeline-wrapper__accordian--summary"   
          style={{ cursor: 'pointer' }}
        >
          <Link 
            to={`/app/course/${chapter}/certificate`}
            state={{ courseData }} // Pass course data via state
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FaCertificate style={{ color: 'grey' }} />
                <Typography
                  className="course-timeline-wrapper__title"
                  variant="body2"
                  sx={{ 
                    marginLeft: 17,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}             
                >
                  Certificate
                </Typography>
              </Box>
            </Box>
          </Link>
        </AccordionSummary>
      </Accordion>
    );
  };

  return (
    sortedLessonData && (
      <Box className="course-timeline-wrapper">
        {sortedLessonData.map((chapter) => (
          <Accordion
            key={chapter.chapter_id}
            className="course-timeline-wrapper__accordian"
          >
            <AccordionSummary
              className="course-timeline-wrapper__accordian--summary"
              id={`panel${chapter.chapter_id}-header`}
              aria-controls={`panel${chapter.chapter_id}-content`}
              expandIcon={<ExpandMoreIcon />}
            >
              <Box
                component="img"
                src={chapter.isCompleted ? checkCircle : transparentCircle}
                alt="chapter-status-icon"
                className="course-timeline-wrapper__circle-icon"
                id={chapter.isCompleted ? "" : "transparent-circle"}
              />
              <Typography
                className="course-timeline-wrapper__title"
                variant="body2"
              >
                {chapter.chapter_name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Timeline
                className="course-timeline-timeline"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {chapter.lessons.map((lesson, index) => (
                  <TimelineItem
                    key={lesson.lesson_id}
                    className="course-timeline-timeline__item"
                  >
                    <TimelineSeparator>
                      <Box
                        component="img"
                        src={
                          lesson.isCompleted ? checkCircle : transparentCircle
                        }
                        alt="lesson-status-icon"
                        className="course-timeline-wrapper__circle-icon-list"
                        id={lesson.isCompleted ? "" : "transparent-circle"}
                      />
                      {index < chapter.lessons.length - 1 && (
                        <TimelineConnector className="course-timeline-timeline__item--connector" />
                      )}
                    </TimelineSeparator>
                    <TimelineContent className="course-timeline-timeline__item--content">
                      <Box className="timeline-content-wrapper">
                        {lesson.isLocked ? (
                          <Typography className="timeline-content-wrapper__text locked">
                            {lesson.lesson_name}
                          </Typography>
                        ) : (
                          <Link
                            to={`/app/course/${storeSlug}/learn/${lesson.lesson_slug}`}
                            className="timeline-content-wrapper__link"
                            onClick={() => setSelectedLesson(lesson.lesson_id)}
                          >
                            <Typography
                              className={`timeline-content-wrapper__text ${selectedLesson === lesson.lesson_id ? 'selected-lesson' : ''
                                }`}
                              id="cursor"
                            >
                              {lesson.lesson_name}
                            </Typography>
                          </Link>
                        )}
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </AccordionDetails>
          </Accordion>
        ))}
        
        {/* Forum accordion */}
        <Accordion className="course-timeline-wrapper__accordian">
          <AccordionSummary
            className="course-timeline-wrapper__accordian--summary"
            style={{ cursor: 'pointer' }}
          >
            <Link to={`/app/forum/${chapter}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', }}>
                  <MdForum style={{ color: 'grey' }} />
                  <Typography
                    className="course-timeline-wrapper__title"
                    variant="body2"
                    sx={{
                      marginLeft: 15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    Course Forum
                  </Typography>
                </Box>
              </Box>
            </Link>
          </AccordionSummary>
        </Accordion>
        
        {/* Chat room accordion */}
        <Accordion className="course-timeline-wrapper__accordian">
          <AccordionSummary
            className="course-timeline-wrapper__accordian--summary"   
            style={{ cursor: 'pointer' }}
          >
            <Link to={`/app/chat/${chapter}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MdChat style={{ color: 'grey' }} />
                  <Typography
                    className="course-timeline-wrapper__title"
                    variant="body2"
                    sx={{ 
                      marginLeft: 15,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}             
                  >
                    Chat Room
                  </Typography>
                </Box>
              </Box>
            </Link>
          </AccordionSummary>
        </Accordion>

        {/* Render certificate accordion only when course is completed */}
        {/* {renderCertificateAccordion()} */}

        {/* Pinned content accordion */}
        <Accordion className="course-timeline-wrapper__accordian">
          <AccordionSummary
            className="course-timeline-wrapper__accordian--summary"
            style={{ cursor: 'pointer' }}
          >
            <Link to={`/app/course/${chapter}/learn/pinned`}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MdPushPin style={{ color: 'grey' }} />
                  <Typography
                    className="course-timeline-wrapper__title"
                    variant="body2"
                  >
                    Pin
                  </Typography>
                </Box>
              </Box>
            </Link>
          </AccordionSummary>
        </Accordion>
      </Box>
    )
  );
};

export default CourseTimeline;